/**
 * Webtech HR Engine™ - AI Credits Purchase Page
 * Page d'achat de crédits IA via PayTech
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Sparkles,
  Coins,
  ShoppingCart,
  Check,
  Loader2,
  CreditCard,
  Zap,
  Star,
  ArrowLeft,
  History,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  Gift
} from 'lucide-react';

// Format price
const formatPrice = (amount) => {
  return new Intl.NumberFormat('fr-FR').format(amount);
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Status badge component
function StatusBadge({ status }) {
  const statusConfig = {
    paid: { label: 'Payé', class: 'bg-green-100 text-green-700', icon: CheckCircle },
    pending: { label: 'En attente', class: 'bg-amber-100 text-amber-700', icon: Clock },
    failed: { label: 'Échoué', class: 'bg-red-100 text-red-700', icon: XCircle },
    cancelled: { label: 'Annulé', class: 'bg-slate-100 text-slate-700', icon: XCircle },
  };
  
  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;
  
  return (
    <Badge className={config.class}>
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
    </Badge>
  );
}

export default function AICreditsPurchasePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [packs, setPacks] = useState([]);
  const [balance, setBalance] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  
  // Check for success/cancel from PayTech redirect
  const purchaseId = searchParams.get('purchase_id');
  const isSuccess = window.location.pathname.includes('/success');
  const isCancel = window.location.pathname.includes('/cancel');

  useEffect(() => {
    fetchData();
    
    if (isSuccess && purchaseId) {
      checkPurchaseStatus(purchaseId);
    } else if (isCancel && purchaseId) {
      toast.error('Achat annulé');
    }
  }, [purchaseId, isSuccess, isCancel]);

  const fetchData = async () => {
    try {
      const [packsRes, balanceRes, purchasesRes] = await Promise.all([
        api.get('/ai/credits/packs'),
        api.get('/ai/credits/balance'),
        api.get('/ai/credits/purchases')
      ]);
      
      setPacks(packsRes.data.packs || []);
      setBalance(balanceRes.data.balance);
      setPurchases(purchasesRes.data.purchases || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const checkPurchaseStatus = async (id) => {
    try {
      const response = await api.get(`/ai/credits/purchase/${id}`);
      const purchase = response.data.purchase;
      
      if (purchase.status === 'paid') {
        toast.success(`${purchase.credits} crédits IA ajoutés à votre compte !`);
        fetchData(); // Refresh data
      } else if (purchase.status === 'pending') {
        toast.info('Paiement en cours de traitement...');
        // Poll for status
        setTimeout(() => checkPurchaseStatus(id), 3000);
      }
    } catch (error) {
      console.error('Error checking purchase:', error);
    }
  };

  const handlePurchase = async (packId) => {
    setPurchasing(packId);
    try {
      const response = await api.post('/ai/credits/purchase', { packId });
      
      if (response.data.success && response.data.paymentUrl) {
        // Redirect to PayTech
        window.location.href = response.data.paymentUrl;
      } else {
        toast.error('Erreur lors de l\'initialisation du paiement');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'achat');
    } finally {
      setPurchasing(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Crédits IA">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Crédits IA">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </div>

        {/* Current Balance Card */}
        <Card className="bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Solde actuel</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-4xl font-bold">{formatPrice(balance)}</span>
                  <span className="text-purple-200">crédits IA</span>
                </div>
              </div>
              <div className="p-4 bg-white/20 rounded-2xl">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Packs Section */}
        <div>
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-purple-600" />
            Acheter des crédits
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {packs.map((pack) => (
              <Card 
                key={pack.id}
                className={`relative overflow-hidden transition-all hover:shadow-lg ${
                  pack.popular 
                    ? 'border-2 border-purple-500 shadow-purple-100' 
                    : 'border border-slate-200'
                }`}
              >
                {pack.popular && (
                  <div className="absolute top-0 right-0">
                    <Badge className="rounded-none rounded-bl-lg bg-purple-500 text-white">
                      <Star className="w-3 h-3 mr-1" />
                      Populaire
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{pack.name}</CardTitle>
                  <CardDescription>{pack.description}</CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Coins className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-slate-900">{pack.credits}</p>
                      <p className="text-sm text-slate-500">crédits</p>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="flex items-baseline justify-between">
                    <div>
                      <span className="text-2xl font-bold text-slate-900">
                        {formatPrice(pack.price)}
                      </span>
                      <span className="text-slate-500 ml-1">F CFA</span>
                    </div>
                    {pack.savings && (
                      <Badge className="bg-green-100 text-green-700">
                        <Gift className="w-3 h-3 mr-1" />
                        -{pack.savings}
                      </Badge>
                    )}
                  </div>
                  
                  <p className="text-xs text-slate-500">
                    {formatPrice(Math.round(pack.price / pack.credits))} F CFA / crédit
                  </p>
                  
                  <Button
                    onClick={() => handlePurchase(pack.id)}
                    disabled={purchasing !== null}
                    className={`w-full ${
                      pack.popular 
                        ? 'bg-purple-600 hover:bg-purple-700' 
                        : 'bg-slate-900 hover:bg-slate-800'
                    }`}
                    data-testid={`buy-pack-${pack.id}`}
                  >
                    {purchasing === pack.id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CreditCard className="w-4 h-4 mr-2" />
                    )}
                    Acheter
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <p className="text-xs text-slate-500 mt-4 text-center">
            Paiement sécurisé par PayTech • Orange Money, Wave, Carte bancaire
          </p>
        </div>

        {/* Purchase History */}
        {purchases.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5 text-slate-600" />
                Historique des achats
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {purchases.map((purchase) => {
                  const pack = packs.find(p => p.id === purchase.packId);
                  return (
                    <div 
                      key={purchase.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          purchase.status === 'paid' ? 'bg-green-100' : 'bg-slate-200'
                        }`}>
                          {purchase.status === 'paid' ? (
                            <TrendingUp className="w-4 h-4 text-green-600" />
                          ) : (
                            <Clock className="w-4 h-4 text-slate-500" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {pack?.name || `${purchase.credits} crédits`}
                          </p>
                          <p className="text-xs text-slate-500">
                            {formatDate(purchase.createdAt)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-medium text-slate-900">
                            +{purchase.credits} crédits
                          </p>
                          <p className="text-xs text-slate-500">
                            {formatPrice(purchase.amount)} F CFA
                          </p>
                        </div>
                        <StatusBadge status={purchase.status} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Section */}
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-6">
            <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              Comment fonctionnent les crédits IA ?
            </h3>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Chaque action IA (analyse KPI, suggestions, rapports) consomme des crédits
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Les crédits n'expirent jamais et sont cumulables
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Plus le pack est grand, plus le prix par crédit est avantageux
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Paiement sécurisé via Orange Money, Wave ou carte bancaire
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
